import type { UserRole } from './types';

/**
 * Single source of truth for who may open which page.
 *
 * Both the eyebrow menu and the router read from here. Keeping two lists in
 * sync by hand is exactly the kind of drift that produces a menu entry leading
 * to a 403 — or worse, a page that is quietly reachable after it was supposed
 * to be restricted.
 *
 * **This is UI gating, not enforcement.** The backend decides what actually
 * happens, and for two of these it is currently *more permissive on reads* than
 * this table — see the notes below. So a page hidden here is hidden, not
 * protected: someone with a token and curl can still read those lists.
 *
 * Verified against production per route (not by grepping the file — an earlier
 * version of this table was wrong precisely because a whole-file grep found
 * `requireRole('ADMIN')` on the *write* routes and assumed it covered the list
 * route too).
 */
export const ROUTE_ROLES: Record<string, UserRole[]> = {
  // routes/domains.routes.ts — GET hat requireAuth, Schreibzugriffe
  // requireRole('ADMIN','OPERATIONS'). Hier also lesend fuer alle, die
  // angemeldet sind: die Uebersicht enthaelt keine Zugangsdaten, sondern
  // oeffentliche DNS-Angaben. Wer sie aendern will, braucht eine der beiden
  // Rollen -- und die Seite zeigt Schaltflaechen, die dann 403 gaeben.
  // Bewusst nicht versteckt: eine Domainliste ist Betriebswissen, keine
  // Verschlusssache.
  '/domains': ['ADMIN', 'OPERATIONS', 'SUPPORT', 'PRODUCT_MANAGER', 'SALES', 'BILLING', 'MARKETING'],
  // Lesen darf, wer die Infrastruktur sieht. Aendern nicht -- dafuer gibt es
  // die eigene Oberflaeche des Gateways, und dort gelten dessen Rollen.
  '/inference': ['ADMIN', 'OPERATIONS', 'PRODUCT_MANAGER'],
  // Verlagsverbund (Control Plane). routes/verbund.routes.ts prueft jede Route
  // nach backend/src/lib/verbund-rechte.ts -- auch das Lesen: Antraege tragen
  // Namen und Mailadressen von Ansprechpersonen. Was innerhalb der Seite
  // erlaubt ist (Ausschluss nur ADMIN), liefert das Backend in `rechte`.
  '/verbund': ['ADMIN', 'OPERATIONS'],
  // Lesen darf jeder, der die Domains sehen darf. Einen Durchlauf anstossen
  // nicht -- das prueft die Route selbst, weil ein Crawl Last erzeugt.
  '/domains/:id/urls': ['ADMIN', 'OPERATIONS', 'SUPPORT', 'PRODUCT_MANAGER', 'SALES', 'BILLING', 'MARKETING'],
  // Writes are requireRole('ADMIN'). GET / and GET /:id have requireAuth only,
  // so every signed-in role can currently read the staff list — including
  // suspendedReason and who has 2FA switched off. Hidden here by choice; see
  // SECURITY-TODO.md point 20.
  '/users': ['ADMIN'],
  // Git-Konten anzulegen und zu entziehen ist Personalverwaltung; die
  // Registry aufzuraeumen loescht Abzuege, die nur ein neuer Build
  // zurueckbringt. Beide Routen pruefen das auch serverseitig
  // (forgejo.routes.ts und registry.routes.ts), das hier ist nur die Sicht.
  '/git-registry': ['ADMIN', 'OPERATIONS'],
  // Reichweitenzahlen sind Betriebs- und Vertriebswissen, keine
  // Verschlusssache. Die Route prueft dieselben Rollen.
  '/analytics': ['ADMIN', 'OPERATIONS', 'PRODUCT_MANAGER', 'SALES', 'MARKETING'],
  // routes/integrations.routes.ts — requireRole('ADMIN') on every route,
  // including the list. Verified: OPERATIONS gets 403.
  '/integrations': ['ADMIN'],
  // routes/api-keys.routes.ts — requireRole('ADMIN') on every route. Verified.
  '/api-keys': ['ADMIN'],
  // Writes are ADMIN + PRODUCT_MANAGER; GET / is requireAuth only. The keys it
  // returns are publishable (`juni_pk_…`, they sit in marketing-site HTML), so
  // the exposure is configuration, not a secret.
  '/embed-keys': ['ADMIN', 'PRODUCT_MANAGER'],
  // routes/audit.routes.ts — requireRole('ADMIN', 'OPERATIONS'). Verified:
  // PRODUCT_MANAGER and SALES get 403.
  '/audit': ['ADMIN', 'OPERATIONS'],
  // routes/base-stacks.routes.ts — reads are requireAuth, writes are
  // ADMIN/OPERATIONS. Gated to the writers here because the page is an editor.
  '/base-stacks': ['ADMIN', 'OPERATIONS'],
};

/**
 * Die Seiten, die MARKETING sehen soll.
 *
 * ══ WARUM HIER EINE POSITIVLISTE STEHT ═════════════════════════════════════
 *
 * Fuer alle bisherigen Rollen gilt die Regel unter `mayAccess`: was oben nicht
 * gesperrt ist, ist offen. Das passt, solange jede Rolle im Betrieb arbeitet --
 * Operations, Support, Produktmanagement und Vertrieb brauchen den Blick auf
 * Instanzen und Server, auch wenn sie dort nichts aendern duerfen.
 *
 * MARKETING ist die erste Rolle, fuer die das nicht mehr stimmt. Eine
 * Serverliste, eine Deployment-Historie und eine Sicherungsuebersicht helfen
 * dort niemandem; sie stehen nur im Weg und legen Betriebsinterna offen, fuer
 * die es keinen Anlass gibt. Deshalb die Umkehr: fuer MARKETING ist offen, was
 * hier steht.
 *
 * **Das ist weiterhin nur die Sicht, nicht die Sicherung.** Das Backend haelt
 * an seiner Hausregel fest -- lesen darf, wer angemeldet ist --, und 125 seiner
 * 366 Routen tragen nur `requireAuth`. Wer hier eine Seite streicht, verbirgt
 * sie; wer sie wirklich schuetzen will, muss die Route anfassen. Die drei
 * Stellen, an denen das fuer MARKETING geschehen ist, stehen in
 * `backend/src/lib/rollen.ts` unter `OHNE_MARKETING`.
 */
const MARKETING_SEITEN = new Set([
  '/',                    // Dashboard
  '/analytics',
  '/domains',
  '/customers',           // lesend; Stammdaten aendern darf die Rolle auch
  '/instances',           // wegen der Zahlen und der Mailgestaltung je Instanz
  '/instances/:id',
  '/webseiten',
  '/products',            // was verkauft wird, gehoert zur Aussendarstellung
  '/products/:id',
  '/orders',              // lesend
  '/feedback',            // Kundenrueckmeldungen
  '/apps',
  '/instanz-zugang',      // oeffentlicher Einloeselink, rollenlos
  // Der Crawler und seine Ergebnisse. Welche Adressen eine Domain kennt und
  // welche davon antworten, ist die Grundlage jeder Reichweitenarbeit.
  '/domains/:id/urls',
]);

/**
 * Den Schluessel finden, unter dem ein konkreter Pfad in den Tabellen steht.
 *
 * ══ WARUM DAS NOETIG IST ═══════════════════════════════════════════════════
 *
 * Die Tabellen fuehren Muster (`/instances/:id`), der Browser liefert Pfade
 * (`/instances/cmu3v…`). Ein reiner Schluesselzugriff findet dazwischen
 * nichts. Genau daran ist der Eintrag `/domains/:id/urls` von Anfang an
 * vorbeigelaufen: er stand in der Tabelle, aber `ROUTE_ROLES['/domains/abc/urls']`
 * ist `undefined`, und damit war die Seite fuer jeden offen.
 *
 * Ein Segment `:name` trifft genau ein Segment — nicht mehrere, sonst faengt
 * `/instances/:id` auch `/instances/abc/def`.
 */
function schluessel(path: string): string | undefined {
  if (path in ROUTE_ROLES || MARKETING_SEITEN.has(path)) return path;
  const teile = path.split('/');
  for (const muster of [...Object.keys(ROUTE_ROLES), ...MARKETING_SEITEN]) {
    const m = muster.split('/');
    if (m.length !== teile.length) continue;
    if (m.every((seg, i) => seg.startsWith(':') || seg === teile[i])) return muster;
  }
  return undefined;
}

/**
 * Everything not listed above is open to any signed-in staff member. That is
 * deliberate: the operational pages are the job, and their write actions are
 * role-checked individually in the backend.
 *
 * Ausnahme ist MARKETING -- siehe `MARKETING_SEITEN`.
 */
export function mayAccess(path: string, role: UserRole | undefined): boolean {
  const k = schluessel(path);
  const required = k ? ROUTE_ROLES[k] : undefined;
  if (required) return role !== undefined && required.includes(role);
  if (role === 'MARKETING') return k !== undefined && MARKETING_SEITEN.has(k);
  return true;
}

/**
 * Die Rollen, die ein Pfad verlangt — oder `undefined`, wenn er offen ist.
 *
 * Fuer die Fehlerseite, die sagen will, wer hier hindarf.
 */
export function rollenFuer(path: string): UserRole[] | undefined {
  const k = schluessel(path);
  return k ? ROUTE_ROLES[k] : undefined;
}

/** First page the user is actually allowed to see — used after a denied deep link. */
export const FALLBACK_ROUTE = '/';

export const ROLE_LABEL: Record<UserRole, string> = {
  ADMIN: 'Administrator',
  OPERATIONS: 'Operations',
  PRODUCT_MANAGER: 'Product Management',
  SUPPORT: 'Technischer Support',
  SALES: 'Vertrieb',
  BILLING: 'Buchhaltung',
  MARKETING: 'Marketing',
};
